/**
 * The section 12.2 dashboard.
 *
 * Counts, not a live monitor. Everything here answers "is this instance healthy and
 * how much is it being used", which is a question an administrator asks on arriving,
 * not one that needs a websocket.
 */
import { useTranslation } from 'react-i18next'

import { useAdminDashboard, useAdminQueue } from '@/api/admin'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Pill, formatBytes } from '@/components/admin/AdminShell'

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-text-muted">{hint}</p>}
    </div>
  )
}

export function AdminOverview() {
  const { t } = useTranslation(['admin', 'common'])
  const dashboard = useAdminDashboard()
  const queue = useAdminQueue()

  if (dashboard.isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-24" />
        ))}
      </div>
    )
  }

  const data = dashboard.data
  if (!data) return null

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.dashboard.title')}</h2>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label={t('admin.dashboard.users')}
          value={String(data.users_active)}
          hint={
            data.users_locked > 0
              ? t('admin.dashboard.users_locked', { count: data.users_locked })
              : undefined
          }
        />
        <Stat label={t('admin.dashboard.workspaces')} value={String(data.workspaces_total)} />
        <Stat label={t('admin.dashboard.projects')} value={String(data.projects_total)} />
        <Stat label={t('admin.dashboard.sessions')} value={String(data.sessions_active)} />
        <Stat label={t('admin.dashboard.calcs_7d')} value={String(data.calcs_7d)} />
        <Stat label={t('admin.dashboard.calcs_30d')} value={String(data.calcs_30d)} />
        <Stat
          label={t('admin.dashboard.disk')}
          value={formatBytes(data.disk_bytes)}
          hint={t('admin.dashboard.disk_hint')}
        />
        <Stat label={t('admin.dashboard.tickets_open')} value={String(data.tickets_open)} />
        <Stat
          label={t('admin.dashboard.tickets_awaiting')}
          value={String(data.tickets_awaiting)}
          hint={t('admin.dashboard.tickets_awaiting_hint')}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.dashboard.queue')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {queue.data?.inline && (
            <p className="flex items-center gap-2 text-sm">
              <Pill tone="warn">{t('status.warning', { ns: 'common' })}</Pill>
              {t('admin.dashboard.queue_inline')}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat
              label={t('admin.dashboard.queue_queued')}
              value={String(queue.data?.queued ?? 0)}
            />
            <Stat
              label={t('admin.dashboard.queue_rendering')}
              value={String(queue.data?.rendering ?? 0)}
            />
            <Stat
              label={t('admin.dashboard.queue_done')}
              value={String(queue.data?.done_24h ?? 0)}
            />
            <Stat
              label={t('admin.dashboard.queue_failed')}
              value={String(queue.data?.failed_24h ?? 0)}
            />
          </div>

          {/* Section 12.2 asks for "slowest modules". Timings come from the
              `duration_ms` on each calculation's audit entry, over the last day. */}
          <div>
            <p className="mb-2 text-xs text-text-muted">{t('admin.dashboard.slowest')}</p>
            {queue.data && queue.data.slowest_modules.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {queue.data.slowest_modules.map((timing) => (
                  <li key={timing.module} className="flex flex-wrap items-baseline gap-2">
                    {/* A module id is an identifier, never translated (14.4). */}
                    <span className="font-mono text-xs">{timing.module}</span>
                    <span className="text-text-muted">
                      {t('admin.dashboard.slowest_runs', {
                        count: timing.runs,
                        mean: timing.mean_ms,
                        max: timing.max_ms,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted">{t('admin.dashboard.slowest_none')}</p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.dashboard.versions')}</CardTitle>
        </CardHeader>
        <CardBody>
          {/* Versions are identifiers, never translated (14.4). */}
          <dl className="grid gap-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-text-muted">{t('app.name', { ns: 'common' })}</dt>
              <dd className="font-mono">{data.app_version}</dd>
            </div>
            <div>
              <dt className="text-xs text-text-muted">calc</dt>
              <dd className="font-mono">{data.engine_version}</dd>
            </div>
            <div>
              <dt className="text-xs text-text-muted">{t('admin.dashboard.environment')}</dt>
              <dd className="font-mono">{data.environment}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>
    </div>
  )
}
